
import { LuX, LuPackage, LuCalendarDays, LuMapPin, LuFlaskConical, LuTag, LuBoxes, LuBarcode, LuFactory, LuTruck, LuFileText, LuHash } from "react-icons/lu";

const InventoryDetailModel = ({ item, onClose }) => {
  const data = Array.isArray(item) ? item[0] : item;
  if (!data) return null;

  const formatDate = (date) => {
    if (!date) return "Not available";
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return "Not available";
    }
    return parsedDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const isExpired = data.expireDate ? new Date(data.expireDate).getTime() < Date.now() : false;

  const details = [
    { label: "Item ID", value: data.id ? `#${data.id}` : null, icon: LuHash },
    { label: "Type", value: data.type, icon: LuTag },
    { label: "Quantity", value: data.quantity, icon: LuBoxes },
    { label: "Catalog", value: data.catalog, icon: LuBarcode },
    { label: "Manufacturer", value: data.manufacturer, icon: LuFactory },
    { label: "Supplier", value: data.supplier, icon: LuTruck },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) { onClose?.(); }}}
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-50/70 px-4 py-3.5 sm:px-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
            <LuPackage size={20} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="max-w-full truncate text-lg font-bold text-slate-900">
                {data.item || "Unnamed Item"}
              </h2>

              <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase ${
                  isExpired ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"
                }`}
              >
                {isExpired ? "Expired" : "Active"}
              </span>
            </div>

            <p className="text-xs text-slate-500"> Inventory item details </p>
          </div>

          <button type="button" onClick={onClose} aria-label="Close modal"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
          >
            <LuX size={19} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          <Section title="Item Overview">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {details.map((detail) => (
                <DetailCard key={detail.label} label={detail.label} value={detail.value} icon={detail.icon} />
              ))}
            </div>
          </Section>

          <Section title="Storage & Project">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <DetailCard label="Location" value={data.location} icon={LuMapPin} />
              <DetailCard label="Project" value={data.project} icon={LuFlaskConical} />
            </div>
          </Section>

          <Section title="Timeline">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <DetailCard label="Added Date" value={formatDate(data.addDate)} icon={LuCalendarDays} />
              <DetailCard label="Expiry Date" value={formatDate(data.expireDate)} icon={LuCalendarDays} danger={isExpired} />
            </div>
          </Section>

          <Section title="Documents" last>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <DetailCard label="Purchase Order" value={data.poDoc} icon={LuFileText} />
              <DetailCard label="Quotation" value={data.quotationDoc} icon={LuFileText} />
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
          <div className="flex justify-end">
            <button type="button" onClick={onClose}
              className="w-full rounded-lg bg-slate-900 px-5 py-2 text-sm font-medium text-white transition hover:bg-slate-800 sm:w-auto"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children, last = false }) => {
  return (
    <section className={last ? "" : "mb-5"}>
      <div className="mb-2.5 flex items-center gap-3">
        <h3 className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {title}
        </h3>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      {children}
    </section>
  );
};

const DetailCard = ({ label, value, icon: Icon, danger = false }) => {
  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <div className={`flex min-w-0 items-center gap-3 rounded-xl border p-3 transition ${
        danger ? "border-red-200 bg-red-50" : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
    >
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg 
            ${ danger ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-500" }`}
      >
        <Icon size={16} />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-slate-400">
          {label}
        </p>

        <p className={`break-words text-sm ${
            hasValue ? danger ? "font-semibold text-red-700" : "font-semibold text-slate-800" : "text-slate-400"
          }`}
          title={hasValue ? String(value) : undefined}
        >
          {hasValue ? value : "Not available"}
        </p>
      </div>
    </div>
  );
};

export default InventoryDetailModel;